import { Permission } from '@today/types'
import { useState } from 'react'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useTenantSlug } from '@/hooks/products/useTenantLocales'
import { useTenantOnboarding } from '@/hooks/useTenantOnboarding'
import { shouldShowResumeBanner } from '@/modules/onboarding/wizard-navigation'

const dismissKey = (slug: string) => `today.onboarding.resumeDismissed.${slug}`

export interface OnboardingGate {
	/** Still deciding — render nothing yet. */
	isPending: boolean
	/** Where to send the viewer instead of the shell (REQ-008), or null. */
	redirectTo: string | null
	/** The "Resume onboarding" target while the banner shows (REQ-011), or null. */
	resumeHref: string | null
	/** Hides the banner for the rest of the browser session. */
	dismissResume: () => void
}

/**
 * The admin shell's onboarding gate (TO-190 REQ-008, REQ-011). Only a viewer
 * holding `tenant.manage_settings` — the atom the wizard and its endpoints
 * require (TO-733) — reads their own tenant's onboarding: an un-onboarded tenant
 * redirects to the wizard, an onboarded one with optional steps left gets the
 * resume banner. A failed read never redirects.
 */
export function useOnboardingGate(): OnboardingGate {
	const { permissions, isLoading } = useViewerPermissions()
	const isSettingsHolder = !isLoading && permissions.includes(Permission.TENANT_MANAGE_SETTINGS)
	const { slug, isError: slugFailed } = useTenantSlug()
	const query = useTenantOnboarding(isSettingsHolder ? slug : null)
	const [dismissed, setDismissed] = useState(() => (slug ? sessionStorage.getItem(dismissKey(slug)) !== null : false))

	const none = { redirectTo: null, resumeHref: null, dismissResume: () => undefined }
	if (!isSettingsHolder) return { isPending: isLoading, ...none }
	// A failed slug or onboarding read never redirects: the shell renders as before.
	// No own tenant (a tenant-less Super-Admin) means no onboarding to gate on —
	// the gate never addresses any tenant but the viewer's own.
	if (slugFailed || !slug || query.isError) return { isPending: false, ...none }
	const onboarding = query.data
	if (!onboarding) return { isPending: true, ...none }

	const wizard = `/t/${encodeURIComponent(slug)}/onboarding`
	if (onboarding.completedAt === null) return { isPending: false, ...none, redirectTo: wizard }
	const hidden = dismissed || sessionStorage.getItem(dismissKey(slug)) !== null
	return {
		isPending: false,
		redirectTo: null,
		resumeHref: !hidden && shouldShowResumeBanner(onboarding) ? wizard : null,
		dismissResume: () => {
			sessionStorage.setItem(dismissKey(slug), '1')
			setDismissed(true)
		},
	}
}
